/**
 * Order Action Components
 * Action buttons and controls for order management.
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * Order Actions Component
 * View/Edit button for orders with role-based navigation
 */
export const OrderActions = ({ order, processors, onUpdate, user, customers, onRefresh }) => {
  const navigate = useNavigate();
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const handleAssign = async () => {
    if (selectedProcessor) {
      try {
        const processor = processors.find(p => p.id === selectedProcessor);
        const processorName = processor?.full_name || 'processor';
        
        await axios.post(`${API}/orders/${order.id}/assign`, {
          assignee_id: selectedProcessor
        });
        
        toast.success(`Order assigned to ${processorName}`, {
          description: `Order ${order.order_number} has been successfully assigned.`,
          duration: 4000
        });

        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        console.error('Assignment failed:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to assign order';
        toast.error(errorMessage);
      }
    }
  };

  const handleStatusChange = () => {
    if (newStatus) {
      onUpdate(order.id, { status: newStatus });
    }
  };

  const handleViewOrder = () => {
    // Team Leaders should navigate to full order details page (like processors)
    // Feb 25, 2026: TLs land on Summary (billing-free) by default; Fulfillment
    // remains one tab click away for processing actions.
    if (user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader') {
      navigate(`/orders/${order.id}/summary`);
    } else {
      // Order Desk sees modal
      setShowOrderDetails(true);
    }
  };

  return (
    <>
      <div className="flex items-center justify-start w-full">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleViewOrder}
          className="text-xs px-2.5 py-0.5 h-6 min-w-[60px] whitespace-nowrap font-medium"
          data-testid={`view-order-${order.id}`}
        >
          View
        </Button>
      </div>
    </>
  );
};

/**
 * Processor Actions Component
 * Status update controls for processors
 */
export const ProcessorActions = ({ order, onUpdate }) => {
  const [newStatus, setNewStatus] = useState('');

  const handleStatusChange = () => {
    if (newStatus) {
      onUpdate(order.id, { status: newStatus });
    }
  };

  return (
    <div className="flex gap-2">
      <Select onValueChange={setNewStatus}>
        <SelectTrigger className="w-32" data-testid={`status-select-${order.id}`}>
          <SelectValue placeholder="Update status..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="processing">Start Processing</SelectItem>
          <SelectItem value="completed">Mark Complete</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" onClick={handleStatusChange} data-testid={`update-status-${order.id}`}>
        Update
      </Button>
    </div>
  );
};

export default { OrderActions, ProcessorActions };
